import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Brain, MessageSquareText, FileText, Mic, Sparkles, Lock, Zap, Search, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const aiCapabilities = [
  {
    icon: Mic,
    titleKey: 'ai.capabilities.transcription.title',
    descKey: 'ai.capabilities.transcription.description',
    href: '/products/next',
  },
  {
    icon: FileText,
    titleKey: 'ai.capabilities.summary.title',
    descKey: 'ai.capabilities.summary.description',
    href: '/products/next',
  },
  {
    icon: MessageSquareText,
    titleKey: 'ai.capabilities.chat.title',
    descKey: 'ai.capabilities.chat.description',
    href: '/products/zimlet-next',
  },
  {
    icon: Search,
    titleKey: 'ai.capabilities.search.title',
    descKey: 'ai.capabilities.search.description',
    href: '/products/zimbra-sovereign-ai',
  },
  {
    icon: Sparkles,
    titleKey: 'ai.capabilities.assistant.title',
    descKey: 'ai.capabilities.assistant.description',
    href: '/products/zimbra-sovereign-ai',
  },
  {
    icon: Zap,
    titleKey: 'ai.capabilities.automation.title',
    descKey: 'ai.capabilities.automation.description',
    href: '/products/ai-open-source-stack',
  },
];

const aiPrinciples = [
  { icon: Lock, titleKey: 'ai.principles.onprem.title', descKey: 'ai.principles.onprem.description' },
  { icon: Brain, titleKey: 'ai.principles.models.title', descKey: 'ai.principles.models.description' },
  { icon: Sparkles, titleKey: 'ai.principles.gdpr.title', descKey: 'ai.principles.gdpr.description' },
];

interface AICapabilitiesProps {
  accent?: string;
  productName?: string;
}

const AICapabilities: React.FC<AICapabilitiesProps> = ({ accent = 'from-blue-600 to-cyan-600', productName }) => {
  const { t } = useTranslation('editor');

  return (
    <section className="py-20 bg-gradient-to-b from-gray-950 to-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-500/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/25 mb-6">
            <Sparkles className="h-3.5 w-3.5 text-blue-400" />
            <span className="text-blue-300 text-sm font-semibold tracking-wide">{t('ai.badge')}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
            {productName ? t('ai.titleWithProduct', { product: productName }) : t('ai.title')}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">{t('ai.subtitle')}</p>
        </motion.div>

        {/* Capabilities grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {aiCapabilities.map((cap, i) => {
            const Icon = cap.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
              <Link
                to={cap.href}
                className="relative block h-full bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/[0.08] hover:border-cyan-400/40 hover:-translate-y-1 transition-all group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              >
                <ArrowUpRight className="absolute top-6 right-6 h-4 w-4 text-cyan-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-white font-bold mb-2">{t(cap.titleKey)}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{t(cap.descKey)}</p>
              </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Principles — on-premise AI banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-600/10 to-cyan-600/10 border border-blue-500/20 rounded-2xl p-8"
        >
          <div className="grid sm:grid-cols-3 gap-6">
            {aiPrinciples.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">{t(p.titleKey)}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">{t(p.descKey)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Comparison strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-8 grid sm:grid-cols-3 gap-4 text-center"
        >
          <div className="bg-red-500/5 border border-red-500/15 rounded-xl p-5">
            <p className="text-red-400 font-bold text-sm mb-1">Microsoft Copilot</p>
            <p className="text-gray-500 text-xs">{t('ai.comparison.copilot')}</p>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-5">
            <p className="text-amber-400 font-bold text-sm mb-1">Google Gemini</p>
            <p className="text-gray-500 text-xs">{t('ai.comparison.gemini')}</p>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
            <p className="text-green-400 font-bold text-sm mb-1">ScaleNix AI</p>
            <p className="text-gray-300 text-xs">{t('ai.comparison.scalenix')}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AICapabilities;
